// A chapter's numbered references. The markers in the notes are bare `[3]`,
// which the markdown renderer turns into a link down to entry 3 here.
//
// A citation's free-text `textMd` is what a reader sees; `bibliographyId`
// is optional, and points the same citation at one entry in the site-wide
// bibliography, the way a `[[Neh 1:5]]` reference points at a chapter. It is
// read here the way `Prose` reads its own glossary matcher, rather than
// threaded through as a prop, since nothing above this component needs it.
import { citationId, type CommentaryCitationInput } from "@bibleunleashed/shared";
import { Link } from "wouter";
import { Icon } from "@/components/Icon";
import { formatBibliographyEntry, useBibliography } from "@/lib/bibliography";
import { Editable } from "./Editable";

export function Citations({
  citations,
  editing,
  onChange,
  onAdd,
  onMove,
  onRemove,
}: {
  citations: CommentaryCitationInput[];
  editing: boolean;
  onChange: (index: number, change: Partial<CommentaryCitationInput>) => void;
  onAdd: () => void;
  onMove: (index: number, by: -1 | 1) => void;
  onRemove: (index: number) => void;
}) {
  const { data: entries } = useBibliography();
  const entryFor = (id: string | null) => (id ? entries?.find((e) => e.id === id) : undefined);

  if (!editing && !citations.some((c) => c.textMd.trim())) return null;

  return (
    <section className="mt-14 border-t border-rule pt-6">
      <h2 className="mb-4 font-sans text-[11px] tracking-[0.1em] text-ink-3 uppercase">References</h2>
      <ol className="space-y-3">
        {citations.map((c, i) => (
          <li key={i} id={citationId(i + 1)} className="flex gap-3 scroll-mt-20 text-[14px] leading-relaxed text-ink-2">
            <span className="w-6 shrink-0 pt-0.5 text-right font-sans text-[12px] text-ink-3">{i + 1}.</span>
            <div className="min-w-0 flex-1">
              <Editable
                label={`Reference ${i + 1}`}
                plain
                editing={editing}
                value={c.textMd}
                placeholder="Author, “Title,” publication, page."
                onChange={(textMd) => onChange(i, { textMd })}
              />
              {editing ? (
                <>
                  <select
                    value={c.bibliographyId ?? ""}
                    onChange={(e) => onChange(i, { bibliographyId: e.target.value || null })}
                    aria-label={`Bibliography entry for reference ${i + 1}`}
                    className="mt-1.5 w-full rounded border border-rule bg-paper px-2 py-1 font-sans text-[12px] text-ink focus:border-accent focus-visible:outline-none"
                  >
                    <option value="">No bibliography entry</option>
                    {entries?.map((e) => (
                      <option key={e.id} value={e.id}>
                        {formatBibliographyEntry(e)}
                      </option>
                    ))}
                  </select>
                  <div className="mt-1.5 flex items-center gap-2">
                    <input
                      value={c.url ?? ""}
                      onChange={(e) => onChange(i, { url: e.target.value.trim() || null })}
                      placeholder="https://… (optional)"
                      aria-label={`Link for reference ${i + 1}`}
                      className="min-w-0 flex-1 rounded border border-rule bg-paper px-2 py-1 font-sans text-[12px] text-ink focus:border-accent focus-visible:outline-none"
                    />
                    <button
                      onClick={() => onMove(i, -1)}
                      disabled={i === 0}
                      aria-label={`Move reference ${i + 1} up`}
                      className="grid size-6 place-items-center rounded text-ink-3 hover:bg-paper-3 hover:text-ink disabled:opacity-30"
                    >
                      <Icon name="up" width="14" height="14" />
                    </button>
                    <button
                      onClick={() => onMove(i, 1)}
                      disabled={i === citations.length - 1}
                      aria-label={`Move reference ${i + 1} down`}
                      className="grid size-6 place-items-center rounded text-ink-3 hover:bg-paper-3 hover:text-ink disabled:opacity-30"
                    >
                      <Icon name="down" width="14" height="14" />
                    </button>
                    <button
                      onClick={() => onRemove(i)}
                      aria-label={`Delete reference ${i + 1}`}
                      className="grid size-6 place-items-center rounded text-ink-3 hover:bg-paper-3 hover:text-ink"
                    >
                      <Icon name="trash" width="14" height="14" />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {entryFor(c.bibliographyId) && (
                    <Link
                      href={`/bibliography#${c.bibliographyId}`}
                      className="mt-0.5 block truncate font-sans text-[12px] text-accent"
                    >
                      {formatBibliographyEntry(entryFor(c.bibliographyId)!)}
                    </Link>
                  )}
                  {c.url && (
                    <a href={c.url} target="_blank" rel="noopener noreferrer" className="mt-0.5 block truncate font-sans text-[12px] text-accent">
                      {c.url}
                    </a>
                  )}
                </>
              )}
            </div>
          </li>
        ))}
      </ol>
      {editing && (
        <button
          onClick={onAdd}
          className="mt-4 flex items-center gap-1.5 rounded-md border border-dashed border-rule px-3 py-1.5 text-[12px] text-ink-2 hover:border-ink-3 hover:text-ink"
        >
          <Icon name="plus" width="14" height="14" /> Add a reference
        </button>
      )}
    </section>
  );
}
